import { SideDetailsLayout } from 'components/Layout/SideDetailsLayout';
import { SearchStringFilter } from 'components/SearchStringFilter';
import { TableLoader } from 'components/TableLoader';
import { getColumnId, TableSettings } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { TotalCount } from 'components/TotalCount';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderStatusEnum, WorkOrderPriceStatusEnum, ContractStatusEnum } from 'gql/graphql';
import { ChapterEnum, CostGroupEnum, SectionEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSelection } from 'hooks/useSelection';
import { useTableCollapse } from 'hooks/useTableCollapse';
import { CodeCell } from 'pages/Project/Common/CodeCell';
import type { WorkOrderPriceListItem } from 'pages/Project/UerProject/WorkOrder';
import { useAdditionalBaseCols } from 'pages/Project/UerProject/WorkOrder/hooks/useAdditionalBaseCols';
import { usePriceGetAdditionalClassName } from 'pages/Project/UerProject/WorkOrder/hooks/usePriceGetAdditionalClassName';
import { getTagLabel } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { useLinkActs } from 'pages/Project/UerProject/WorkOrder/WorkOrderOthersTable/useLinkActs';
import { Table, Loader, Button, Grid, GridItem, TableTextFilter } from 'uikit';
import { getCheckedIdsArr } from 'utils';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useWorkOrderVersionOthersActColumns } from '../hooks/useWorkOrderVersionOthersActColumns';
import { useWorkOrderVersionOthersSeasonsColumns } from '../hooks/useWorkOrderVersionOthersSeasonsColumns';
import { useWorkOrderVersionSeasons } from '../hooks/useWorkOrderVersionSeasons';
import { gqlWorkOrderVersionActsKC2Version } from '../workOrderVersionQueries';
import { WorkOrderVersionSummary } from '../WorkOrderVersionSummary';

import { getGroupedOtherItems } from './otherHelpers';
import { getWorkOrderVersionOtherPriceList } from './workOrderVersionOtherTableQueries';

const titleWithTooltipColumnsUnitCost = () => (
    <TitleWithTooltipColumns
        title="Стоимость единицы в текущем уровне цен, руб. без НДС"
        childTooltip="Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
    />
);

const titleWithTooltipColumnsTotalPlanCost = () => (
    <TitleWithTooltipColumns
        title="Стоимость всего, руб. без НДС"
        childTooltip="Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
    />
);

interface IWorkOrderVersionOtherTableProps {
    workOrderId: number;
    workOrderObjectId: number;
    section: SectionEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    reload?: any;
    isDisabledByProjectStatus?: boolean;
}

type Row = Partial<WorkOrderPriceListItem>;

export const WorkOrderVersionOtherTable: React.FC<IWorkOrderVersionOtherTableProps> = ({
    workOrderId,
    workOrderObjectId,
    workOrderStatus,
    reload,
    contractStatus,
    isDisabledByProjectStatus,
}) => {
    const { id: projectId } = useParams();
    const dispatch = useDispatch();

    const [tableFilters, setTableFilters] = useState<{ code?: string; name?: string }>({});

    const variables = useMemo(
        () => ({
            workOrderVersionId: workOrderId,
            workOrderVersionObjectId: workOrderObjectId,
        }),
        // eslint-disable-next-line
        [workOrderId, workOrderObjectId, reload],
    );

    const actsVariables = useMemo(
        () => ({
            costGroup: CostGroupEnum.Others,
            workOrderVersionId: workOrderId,
            workOrderVersionObjectId: workOrderObjectId,
        }),
        // eslint-disable-next-line
        [workOrderId, workOrderObjectId, reload],
    );

    const [workOrderOtherResultData, workOrderOtherResultStatus, , workOrderOtherResultRequest] = useRequest({
        requestId: 'workOrderVersionOtherPriceList',
        query: getWorkOrderVersionOtherPriceList,
        variables,
        withEffect: Boolean(workOrderId),
    });

    const [actsKC2VersionResultData] = useRequest({
        requestId: 'workOrderVersionActsKC2Version',
        query: gqlWorkOrderVersionActsKC2Version,
        variables: actsVariables,
        withEffect: Boolean(workOrderId),
    });

    const [makeResetOtherResultData] = useResetStoreData({ requestId: 'workOrderVersionOtherPriceList' });

    useEffect(() => {
        makeResetOtherResultData(null);
    }, [variables, makeResetOtherResultData]);

    const reloadHandler = useCallback(() => {
        dispatch(workOrderOtherResultRequest(variables));
        setCheckedIds({});
    }, [dispatch, variables, workOrderOtherResultRequest]);

    const { refreshProject } = useProjectData(projectId, false);

    const reloadTree = useCallback(() => {
        reloadHandler();
        refreshProject();
    }, [refreshProject, reloadHandler]);

    const isLoading = workOrderOtherResultStatus === RequestStatus.FETCHING;

    const workOrderResult = useMemo(
        () => workOrderOtherResultData?.uerWorkOrderVersionOtherPriceList || {},
        [workOrderOtherResultData],
    );

    const actsKC2 = useMemo(
        () => actsKC2VersionResultData?.uerWorkOrderVersionActsKC2Version ?? [],
        [actsKC2VersionResultData?.uerWorkOrderVersionActsKC2Version],
    );

    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [isSummaryDetailsVisible, setIsSummaryDetailsVisible] = useState(false);

    const onSummaryDetailsVisibilityChange = useCallback((isVisible) => {
        setIsSummaryDetailsVisible(isVisible);
    }, []);

    const baseColumns = useMemo(
        () => [
            {
                title: 'Статус',
                accessor: 'status',
                align: 'center',
                width: 100,
                renderCell: (row: Row) => row.status && <CodeCell status={row.status} />,
            },
            {
                title: 'Шифр',
                accessor: 'code',
                width: 70,
                align: 'center',
            },
            {
                title: 'Наименование',
                accessor: 'name',
                width: 400,
            },
            {
                title: 'Ед. изм.',
                accessor: 'unit',
                align: 'center',
                width: 80,
            },
            {
                hiddenTitle: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                accessor: 'unitCost',
                align: 'right',
                width: 172,
                control: titleWithTooltipColumnsUnitCost,
            },
            {
                title: 'План по НЗ',
                accessor: 'plan',
                columns: [
                    {
                        title: 'Объем всего',
                        accessor: 'totalPlanVolume',
                        align: 'right',
                        width: 112,
                    },
                    {
                        accessor: 'totalPlanCost',
                        align: 'right',
                        width: 124,
                        control: titleWithTooltipColumnsTotalPlanCost,
                        renderCell: (row: { totalPlanCost: number }) => (
                            <span style={{ fontWeight: 'bold' }}>{row.totalPlanCost}</span>
                        ),
                    },
                ],
            },
        ],
        [],
    ) as any;

    const actColumns = useWorkOrderVersionOthersActColumns(
        actsKC2,
        reloadTree,
        contractStatus,
        workOrderStatus,
        isDisabledByProjectStatus,
    );

    const [seasons] = useWorkOrderVersionSeasons(workOrderId);

    const seasonColumns = useWorkOrderVersionOthersSeasonsColumns(seasons ?? []);

    const additionalBaseCols = useAdditionalBaseCols(titleWithTooltipColumnsTotalPlanCost, workOrderStatus);

    const filtersConfig = useMemo(
        () => [
            {
                id: 'name',
                name: 'Наименование',
                field: 'name',
                filterer: R.T,
                component: { name: SearchStringFilter },
            },
            {
                id: 'status',
                name: 'Статус',
                field: 'status',
                filterer: () => true,
                component: {
                    name: TableTextFilter,
                    props: {
                        items: [
                            { name: 'Черновик', value: 'DRAFT' },
                            { name: 'Новая запись', value: 'NEW_RECORD' },
                        ],
                    },
                },
            },
        ],
        [],
    ) as any[];

    const groupedData = useMemo(
        () =>
            getGroupedOtherItems({
                otherPriceList: workOrderResult?.items ?? [],
                actsKC2,
                tableFilters,
            }),
        [workOrderResult?.items, actsKC2, tableFilters],
    );

    const [columnsWithCollapse, rows] = useTableCollapse<{ id: string; nestLevel: number }>(
        'orderTable',
        baseColumns,
        groupedData as any,
    );

    const linkedSeasons = useLinkActs({ seasonColumns, actColumns });

    const optionalColumns = useMemo(
        () => [...columnsWithCollapse, ...additionalBaseCols, ...linkedSeasons],
        [additionalBaseCols, columnsWithCollapse, linkedSeasons],
    );

    const getAdditionalClassName = usePriceGetAdditionalClassName(
        workOrderOtherResultData?.uerWorkOrderVersionOtherPriceList,
        ChapterEnum.Others,
        workOrderStatus,
    );

    const [selection, , , setSelection] = useSelection('WorkOrderOtherTableVersion');
    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            const hidden = optionalColumns.reduce(
                (acc, column) => {
                    acc[getColumnId(column)] = false;

                    return acc;
                },
                {} as Record<string, false>,
            );

            setSelection({ ...hidden, ...visible });
        },
        [optionalColumns, setSelection],
    );

    const columns = useMemo(
        () => optionalColumns.filter((column) => isColumnVisible(getColumnId(column))),
        [isColumnVisible, optionalColumns],
    );

    const checkedIdsArr: number[] = getCheckedIdsArr(checkedIds);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const rowCount = useMemo(
        () =>
            rows.reduce((counter, row) => {
                if (row.nestLevel) {
                    counter += 1;
                }

                return counter;
            }, 0),
        [rows],
    );

    const handleTableFilterUpdated = useCallback((selectedFilters: any) => {
        setTableFilters((filtersState: any) =>
            R.pickBy(R.is(String), {
                ...filtersState,
                name: selectedFilters.name?.value,
                status:
                    selectedFilters.status?.value?.length === 1
                        ? selectedFilters.status?.value?.map(
                              (val: { name: string; value: WorkOrderPriceStatusEnum }) => val.value,
                          )[0]
                        : [],
            }),
        );
    }, []);

    return (
        <>
            {isLoading && <Loader />}
            <>
                <Grid cols={2} className="TableSettings">
                    <GridItem>
                        {checkedIdsArr.length ? (
                            <Grid cols={4} yAlign="center">
                                <GridItem>
                                    <TotalCount
                                        totalCount={checkedIdsArr.length}
                                        forms={['строка', 'строки', 'строк']}
                                        prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                    />
                                </GridItem>
                                <GridItem col={3}>
                                    <Button label="Сбросить" view="ghost" size="xs" onClick={handleClearChecked} />
                                </GridItem>
                            </Grid>
                        ) : (
                            <TotalCount totalCount={rowCount} />
                        )}
                    </GridItem>
                    <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                        <TableSettings
                            columns={optionalColumns}
                            onConfirm={handleTableSettingsConfirm}
                            isColumnVisible={isColumnVisible}
                        />
                    </GridItem>
                </Grid>
                <SideDetailsLayout
                    onDetailsVisibilityChange={onSummaryDetailsVisibilityChange}
                    collapsedTitle="Показать итоги"
                    expandedTitle="Скрыть итоги"
                >
                    <Table
                        columns={columns}
                        rows={rows}
                        filters={filtersConfig}
                        onFiltersUpdated={handleTableFilterUpdated}
                        getAdditionalClassName={getAdditionalClassName}
                        getTagLabel={getTagLabel}
                        size="s"
                        className="workOrderPrice workOrderOthers"
                        stickyHeader
                        verticalAlign="center"
                        emptyRowsPlaceholder={<TableLoader requestStatus={RequestStatus.SUCCESS} />}
                        borderBetweenColumns
                    />
                    <WorkOrderVersionSummary
                        section={SectionEnum.CustomerLimits}
                        chapter={null}
                        workOrderObjectId={workOrderObjectId}
                        actsKc2={actsKC2}
                        isVisible={isSummaryDetailsVisible}
                    />
                </SideDetailsLayout>
            </>
        </>
    );
};

WorkOrderVersionOtherTable.displayName = 'WorkOrderVersionOtherTable';
